// keepRateStore — persistência + agregação do Keep Rate (recuperação E5/D).
//
// O motor puro (keepRate.js) sempre existiu em main SEM produtor nem consumidor
// (#377 removeu o dashboard morto; a EvalTab ficou com empty-state honesto).
// Este store fecha o ciclo de verdade:
//   • PRODUTOR: onLoopToolCall — todo write_file/apply_edit do loop vira amostra
//     (via attributeDiff do Glass Box); hash-only, sem PII (E4-WS1-T04).
//   • PERSISTÊNCIA: localStorage (cap LIMIT amostras) — as janelas 1h/1d/7d só
//     são reais se a amostra sobrevive ao reload do app.
//   • CONSUMIDOR: measureAndAggregate re-mede contra o arquivo ATUAL (readFile)
//     e agrega no shape da EvalTab do TasksPanel ({keepRate, bars}).
// Oracle pass-rate (1ª via) segue honestamente ausente (passRate null) até
// existir um produtor de benchmark — a EvalTab esconde o stat nulo.
//
// DI de storage/now para testes; nunca lança (telemetria não pode derrubar o loop).

import { attributeDiff, measureSample, keepRate } from './keepRate.js';
import { KEEP_RATE_WINDOWS } from './s5Contracts.js';

export const KEEP_RATE_LS_KEY = 'apolo:keepRate:v1';
export const KEEP_RATE_LIMIT = 200;

function defaultStorage() {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function loadSamples(storage = defaultStorage()) {
  try {
    const raw = storage?.getItem?.(KEEP_RATE_LS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveSamples(samples, storage = defaultStorage()) {
  try {
    storage?.setItem?.(KEEP_RATE_LS_KEY, JSON.stringify(samples.slice(-KEEP_RATE_LIMIT)));
  } catch {
    /* quota/private-mode: telemetria é best-effort */
  }
}

/**
 * PRODUTOR — chame com todo evento do loop; filtra write_file/apply_edit e
 * anexa a amostra (hash-only) ao storage. Retorna a amostra ou null.
 */
export function onLoopToolCall(evt, { sessionId = '', root = '', now = Date.now(), storage = defaultStorage() } = {}) {
  try {
    if (!evt || evt.type !== 'tool_call') return null;
    if (evt.name !== 'write_file' && evt.name !== 'apply_edit') return null;
    const sample = attributeDiff(evt.name, evt.args || {}, {
      diffId: `${sessionId}:${evt.id || now}`,
      generatedAt: now,
    });
    if (!sample) return null;
    // E4-T01 — amostra carrega o ROOT: medir contra OUTRO worktree marcava
    // código mergeado como "morto" (o arquivo não existe lá) e vice-versa.
    if (root) sample.root = String(root);
    const samples = loadSamples(storage);
    samples.push(sample);
    saveSamples(samples, storage);
    return sample;
  } catch {
    return null;
  }
}

/**
 * CONSUMIDOR — re-mede as amostras contra o conteúdo atual (readFile(path) →
 * string|null) e agrega no shape da EvalTab: { passRate:null, keepRate, bars }.
 * null enquanto NENHUMA janela tem amostra elegível (mantém o empty-state).
 *
 * @param {{ now?: number, readFile?: (path: string) => Promise<string|null>, root?: string, storage?: any }} [opts]
 */
export async function measureAndAggregate({ now = Date.now(), readFile, root = '', storage = defaultStorage() } = {}) {
  try {
    let samples = loadSamples(storage);
    if (samples.length === 0) return null;
    // E4-T01 — só amostras DESTE root são medidas/agregadas (o readFile lê no
    // root corrente; medir amostra de outro worktree dá veredito errado).
    // Amostra legada sem root passa (back-compat: não zera dados existentes).
    const inScope = (s) => !root || !s.root || s.root === root;
    if (typeof readFile === 'function') {
      samples = await Promise.all(
        samples.map(async (s) => {
          if (!inScope(s)) return s; // intocada: pertence a outro root
          const content = await readFile(s.file);
          const currentLines = content == null ? null : String(content).split('\n');
          return measureSample(s, { now, currentLines });
        }),
      );
      saveSamples(samples, storage); // persiste os survived re-medidos
    }
    const scoped = samples.filter(inScope);
    if (scoped.length === 0) return null;
    const bars = [];
    for (const w of KEEP_RATE_WINDOWS) {
      const r = keepRate(scoped, w, now);
      if (r != null) {
        const pct = Math.round(r * 100);
        bars.push({ label: `keep ${w}`, pct, win: pct >= 50 });
      }
    }
    if (bars.length === 0) return null;
    const seven = keepRate(scoped, '7d', now);
    return {
      passRate: null, // oracle pass-rate: sem produtor de benchmark — honesto
      keepRate: seven != null ? Math.round(seven * 100) : Math.round((bars[bars.length - 1].pct)),
      bars,
    };
  } catch {
    return null;
  }
}
